import { ErrorMessage, useFormikContext } from 'formik';
import { useState } from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@src/design-system/command';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import { Label } from '@src/design-system/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import type { TReminder } from '@src/shared/types/reminder';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';

import type { TCreateReminderFormValues } from '../schema';

type StageReminderTypeSelectProps = {
  allowedTypes: TReminder['type'][];
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
};

export function StageReminderTypeSelect({
  allowedTypes,
  label = 'Rule Type',
  placeholder = 'Select type',
  searchPlaceholder = 'Search for types',
}: StageReminderTypeSelectProps) {
  const [open, setOpen] = useState(false);
  const { values, setFieldValue, setFieldTouched, errors, touched } =
    useFormikContext<TCreateReminderFormValues>();

  const selectedType = values.type;
  const selectedLabel = selectedType
    ? formatTextForDisplay(selectedType)
    : placeholder;

  const handleSelect = (type: TReminder['type']) => {
    setFieldValue('type', type);
    setFieldTouched('type', true, false);
    setOpen(false);
  };

  return (
    <div>
      <Label>{label}</Label>
      <Popover
        modal
        open={open}
        onOpenChange={setOpen}
      >
        <PopoverTrigger
          type='button'
          {...(errors.type && touched.type && { 'data-error': 'error' })}
          className={cn(
            inputVariants({ variant: 'fill', size: 'lg' }),
            'mt-2 flex w-full cursor-pointer items-center justify-between text-sm',
            !selectedType && 'text-gray-400'
          )}
        >
          <span className='flex items-center gap-2'>{selectedLabel}</span>
        </PopoverTrigger>
        <PopoverContent
          align='start'
          className='w-[var(--radix-popover-trigger-width)] p-0'
        >
          <Command>
            <CommandInput
              containerClassName='rounded-none mb-0 border-0 border-b-1 border-pale-border'
              placeholder={searchPlaceholder}
            />
            <CommandList className='max-h-[min(240px,var(--radix-popover-content-available-height,240px))]'>
              <CommandEmpty>No types found</CommandEmpty>
              <CommandGroup className='p-0'>
                {allowedTypes.map((type) => {
                  const typeLabel = formatTextForDisplay(type);
                  const isSelected = selectedType === type;

                  return (
                    <CommandItem
                      key={type}
                      value={typeLabel}
                      className={cn(
                        'py-2 block overflow-hidden bg-white last:mb-0 border-b border-primary-200 last:border-b-0 rounded-none',
                        isSelected && 'bg-primary-50'
                      )}
                      data-selected={false}
                      onSelect={() => handleSelect(type)}
                    >
                      <p
                        className={cn(
                          'text-sm text-gray-900',
                          isSelected && 'font-medium text-primary-1000'
                        )}
                      >
                        {typeLabel}
                      </p>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ErrorMessage
        name='type'
        component={CustomErrorMessage}
      />
    </div>
  );
}
